import { mapQuotaFieldToDisplayName } from '@/config/plans'
import { type ConsoleUser, type Organization, type PlatformUser, isPlatformUser } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import type {
	ActionsMetricData,
	CombinedOrgMetricsResponse,
	SessionsTenantMetrics,
} from '@/types/organization-owner-api'
import type { NumericQuotaValue, QuotaUsage } from '@/types/quota'
import { isOwnerOfOrganization } from '@/utils/permissions'
import { organizationMetricsApi } from './organizationMetricsApi'

/**
 * Maps API response section key → per_tenant_quota key (max_* field).
 * All these sections follow the same { allowed: number, consumed: number } shape.
 */
const GENERAL_SECTION_MAP: Record<string, string> = {
	devices: 'max_devices',
	dashboards: 'max_dashboards',
	users: 'max_users',
	roles: 'max_roles',
	metadata: 'max_metadata_keys',
	streams: 'max_streams',
	exporters: 'max_exporters',
	apikeys: 'max_api_keys',
	dbc: 'max_parsers',
}

const SESSIONS_METRIC_FIELD_MAP: Record<keyof SessionsTenantMetrics, string> = {
	max_session_rules: 'max_session_rules',
	max_subscriber_groups: 'max_subscriber_groups',
	max_alert_rules: 'max_alert_rules',
	max_session_types: 'max_session_types',
	max_alert_notification_rules: 'max_alert_notification_rules',
}

function isErrorSection(section: unknown): section is { error: string } {
	return typeof section === 'object' && section !== null && 'error' in section
}

function getDefaultQuotaLimits(org: Organization | undefined): Record<string, number> {
	const perTenantQuota = org?.subscription?.quota?.per_tenant_quota
	if (!perTenantQuota) {
		return {}
	}

	const defaults: Record<string, number> = {}
	for (const [key, value] of Object.entries(perTenantQuota)) {
		if (typeof value === 'number') {
			defaults[key] = value
		}
	}
	return defaults
}

export type QuotaTransformAuthContext = {
	org: Organization | undefined
	orgId: string
	user: ConsoleUser | PlatformUser | null
}

export function transformMetricsToQuotaUsage(
	metrics: CombinedOrgMetricsResponse,
	tenantId: string,
	defaults: Record<string, number>,
	ctx: QuotaTransformAuthContext,
): QuotaUsage {
	const { org, orgId, user } = ctx
	const quotas: QuotaUsage['quotas'] = {}

	const setQuota = (maxKey: string, used: number, total: number) => {
		const displayName = mapQuotaFieldToDisplayName(maxKey)
		if (displayName) {
			quotas[displayName] = { used, total } as NumericQuotaValue
		}
	}

	// General sections (all sections contain { allowed, consumed } options)
	for (const [sectionKey, maxKey] of Object.entries(GENERAL_SECTION_MAP)) {
		const section = metrics[sectionKey as keyof CombinedOrgMetricsResponse]

		if (!isErrorSection(section)) {
			const tenantData = (section as Record<string, unknown>)?.[tenantId]
			if (tenantData && typeof tenantData === 'object' && 'allowed' in tenantData && 'consumed' in tenantData) {
				const data = tenantData as { allowed: number; consumed: number }
				setQuota(maxKey, data.consumed, data.allowed)
				continue
			}
		}

		// Fallback: default from subscription
		setQuota(maxKey, 0, defaults[maxKey] ?? 0)
	}

	// Panels per Dashboard (set default as not returned by metrics API as of now)
	// TODO: update this if later backend sends max_panels_per_dashboards in a diff format
	if (defaults.max_panels_per_dashboard) {
		setQuota('max_panels_per_dashboard', 0, defaults.max_panels_per_dashboard)
	}

	// Sessions: GET /metrics/sessions returns tenant-keyed { max_*: { allowed, consumed } }
	if (isErrorSection(metrics.sessions)) {
		setQuota('max_session_rules', 0, defaults.max_session_rules ?? 0)
		setQuota('max_subscriber_groups', 0, defaults.max_subscriber_groups ?? 0)
		setQuota('max_alert_rules', 0, defaults.max_alert_rules ?? 0)
		setQuota('max_session_types', 0, defaults.max_session_types ?? 0)
		setQuota('max_alert_notification_rules', 0, defaults.max_alert_notification_rules ?? 0)
	} else {
		const tenantMetrics = (metrics.sessions as Record<string, SessionsTenantMetrics>)[tenantId]
		if (tenantMetrics) {
			for (const [apiKey, maxKey] of Object.entries(SESSIONS_METRIC_FIELD_MAP)) {
				const field = tenantMetrics[apiKey as keyof SessionsTenantMetrics]
				if (field) {
					setQuota(maxKey, field.consumed, field.allowed)
				}
			}
		}
		for (const [, maxKey] of Object.entries(SESSIONS_METRIC_FIELD_MAP)) {
			const displayName = mapQuotaFieldToDisplayName(maxKey)
			if (displayName && !(displayName in quotas)) {
				setQuota(maxKey, 0, defaults[maxKey] ?? 0)
			}
		}
	}

	// Actions (custom structure: action_types_consumed, actions map, max_actions,..)
	if (isErrorSection(metrics.actions)) {
		setQuota('max_action_types', 0, defaults.max_action_types ?? 0)
		setQuota('max_actions', 0, defaults.max_actions ?? 0)
	} else {
		const actionsData = (metrics.actions as Record<string, ActionsMetricData>)[tenantId]
		if (actionsData) {
			setQuota('max_action_types', actionsData.action_types_consumed, actionsData.max_action_types)
			const totalActionsUsed = Object.values(actionsData.actions).reduce((sum, count) => sum + count, 0)
			setQuota('max_actions', totalActionsUsed, actionsData.max_actions)
		} else {
			setQuota('max_action_types', 0, defaults.max_action_types ?? 0)
			setQuota('max_actions', 0, defaults.max_actions ?? 0)
		}
	}

	// Projects quota is org-scoped (not per-tenant). Synthesize it on every tenant's
	// QuotaUsage so the access-control layer can treat it uniformly with other resources.
	const maxTenants = org?.subscription?.quota?.max_tenants
	const consumedTenants =
		user && isPlatformUser(user) && Array.isArray(user.tenant_list)
			? user.tenant_list.filter(tenant => tenant.org_id === orgId).length
			: 0
	if (typeof maxTenants === 'number') {
		setQuota('max_tenants', consumedTenants, maxTenants)
	}

	return { tenant_id: tenantId, quotas }
}

/** Org metrics are owner-only; non-owners get subscription limits with zero consumption. */
const METRICS_UNAVAILABLE: CombinedOrgMetricsResponse = {
	devices: { error: 'forbidden' },
	dashboards: { error: 'forbidden' },
	users: { error: 'forbidden' },
	roles: { error: 'forbidden' },
	metadata: { error: 'forbidden' },
	apikeys: { error: 'forbidden' },
	actions: { error: 'forbidden' },
	streams: { error: 'forbidden' },
	dbc: { error: 'forbidden' },
	exporters: { error: 'forbidden' },
	sessions: { error: 'forbidden' },
}

function resolveOrganizationForQuota(user: ConsoleUser | PlatformUser | null, orgId: string): Organization | undefined {
	if (!user) {
		return undefined
	}
	if ('organizations' in user && user.organizations?.length) {
		const match = user.organizations.find(o => o.id === orgId)
		if (match) {
			return match
		}
	}
	if (isPlatformUser(user) && user.tenant_list) {
		const tenant = user.tenant_list.find(t => t.org_id === orgId)
		if (tenant?.organization) {
			return tenant.organization
		}
	}
	return user.organizations?.[0]
}

export const quotaApi = {
	getQuotaUsage: async (tenantId: string): Promise<QuotaUsage> => {
		const { user, currentOrganizationId: orgId } = useAuthStore.getState()
		if (!orgId) {
			throw new Error('Organization ID not available for fetching quota usage')
		}

		const org = resolveOrganizationForQuota(user, orgId)
		const defaults = getDefaultQuotaLimits(org)
		const ctx: QuotaTransformAuthContext = { org, orgId, user }

		if (!isOwnerOfOrganization(user, orgId)) {
			return transformMetricsToQuotaUsage(METRICS_UNAVAILABLE, tenantId, defaults, ctx)
		}

		const metrics = await organizationMetricsApi.getAllMetrics(orgId)
		return transformMetricsToQuotaUsage(metrics, tenantId, defaults, ctx)
	},
}
